import React, { useContext, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import PlusIcon from 'assets/img/plus.png';
import PrivilegeCode from 'components/PrivilegeCode/PrivilegeCode';
import Reference from './Reference/Reference';
import CatalogImage from 'components/Common/CatalogImage/CatalogImage';
import Footer from 'components/Common/Footer/Footer';
import ModalLoading from 'components/Modals/ModalLoading/ModalLoading';
import BreadCrumbs from 'components/Common/BreadCrumbs/BreadCrumbs';
import { cartContext } from 'contexts/cartContext';
import { useModalMultiples } from 'hooks/useModal';
import useGtmPageView from 'hooks/gtm/useGtmPageView';
import { CartBodyRequest } from 'providers/CartProvider';
import { newHierarchy } from 'services/data';
import { getRoutesUri } from 'services/generic';
import { pushGtmEvent } from 'services/gtm/gtmEvents';
import { MOBILE } from 'settings/mediaQuery';
import { GTM_PAGE_DIRECT_ORDER } from 'settings/gtm/gtmPageView';
import { getProductData } from 'services/gtm/gtmPageView';
import { formatCartBodyRequestProduct } from 'services/gtm/gtmUtils';

import styles from './DirectOrderPage.module.scss';

export default function DirectOrderPage(): JSX.Element {
  const { addToMyBasket } = useContext(cartContext);
  const navigate = useNavigate();
  const isMobile = useMediaQuery({
    query: MOBILE,
  });
  const { t } = useTranslation(['translation', 'directOrderPage']);
  const [hasReference, setHasReference] = useState<boolean>(false);
  const { open, openModal, closeModal } = useModalMultiples();
  const [references, setReferences] = useState<Array<CartBodyRequest | null>>(
    [...Array(5)].map(() => null)
  );

  useGtmPageView(
    GTM_PAGE_DIRECT_ORDER,
    `Afibel - ${t('LABEL_DIRECT_ORDER_META_TITLE', {
      ns: 'directOrderPage',
    })}`
  );

  function handleAdd() {
    setReferences([...references, null]);
  }

  function mergeReferences(references: CartBodyRequest[]): CartBodyRequest[] {
    const mergedReferences: CartBodyRequest[] = [];

    references.forEach(ref => {
      const indexFound = mergedReferences.findIndex(p => p.sku === ref?.sku);
      if (indexFound !== -1) {
        const totalQty =
          parseInt(mergedReferences[indexFound].qty) + parseInt(ref.qty);

        mergedReferences[indexFound] = {
          ...mergedReferences[indexFound],
          qty: totalQty.toString(),
        };
      } else {
        mergedReferences.push(ref);
      }
    });

    return mergedReferences;
  }

  function handleSubmit() {
    openModal('loading');
    const filteredReferences = references.filter(function (element) {
      return element != null;
    }) as CartBodyRequest[];

    const mergedRefs = mergeReferences(filteredReferences);

    addToMyBasket(filteredReferences, 'commande direct').then(updatedCart => {
      const clearReferences = references.map(() => {
        return null;
      });
      pushGtmEvent(
        {
          event: '_add_to_cart',
          products: mergedRefs.map(r => {
            return {
              ...getProductData({
                ...formatCartBodyRequestProduct(r),
                libelle: 'commande direct',
                couleur: 'commande direct',
                codeLibelleFamille: 'commande direct',
                codeLibelleSsfamille: 'commande direct',
                subuniverse: 'commande direct',
              }),
              add_to_cart_type: 'commande direct',
            };
          }),
        },
        updatedCart
      );
      setReferences(clearReferences);
      closeModal();
      navigate(getRoutesUri<string>('cart'));
    });
  }

  function updateReference(key: number, reference: CartBodyRequest | null) {
    const updatedList = references;
    updatedList[key] = reference;
    setReferences(updatedList);
    setHasReference(!!updatedList.filter(x => x).length);
  }

  if (isMobile) {
    return (
      <div className={styles.container}>
        <RenderMetadata />
        <BreadCrumbs
          breadCrumbsHierarchies={[
            newHierarchy('/', t('LABEL_HOME')),
            newHierarchy('', `${t('LABEL_DIRECT_ORDER')}`),
          ]}
        />
        <div className={styles.header}>
          <div className={styles.content}>
            <div className={styles.title}>
              {t('LABEL_USING_CATALOG_REFERENCES')}
            </div>
            <p>{t('LABEL_USING_CATALOG_REFERENCES_TEXT_1')}</p>
            <p>{t('LABEL_USING_CATALOG_REFERENCES_TEXT_2')}</p>
          </div>
        </div>
        <div className={styles.referencesContainer}>
          {[...Array(references?.length)].map((x, i) => (
            <Reference key={x} row={i} updateReference={updateReference} />
          ))}
          <div className={styles.addReference}>
            <button onClick={handleAdd}>
              <span>{t('LABEL_ENTER_ANOTHER_REFERENCE')}</span>
              <img src={PlusIcon} alt="add" className={styles.plusIcon} />
            </button>
          </div>
        </div>
        <div className={styles.addItems}>
          <button
            className={!hasReference ? styles.disabled : ''}
            onClick={handleSubmit}
          >
            {t('LABEL_ADD_ITEMS_TO_BASKET')}
          </button>
        </div>
        <p className={styles.privilegeDescription}>
          {t('LABEL_PRIVILEGE_CODE_BENEFITTING')}
        </p>
        <PrivilegeCode />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <RenderMetadata />
      <BreadCrumbs
        breadCrumbsHierarchies={[
          newHierarchy('/', t('LABEL_HOME')),
          newHierarchy('', `${t('LABEL_DIRECT_ORDER')}`),
        ]}
      />
      <div className={styles.contentContainer}>
        <div className={styles.leftSide}>
          <div className={styles.header}>
            <CatalogImage
              classes={{
                wrapper: styles.imgWrapper,
                image: styles.catalogImg,
              }}
            />
            <div className={styles.content}>
              <div className={styles.title}>
                {t('LABEL_USING_CATALOG_REFERENCES')}
              </div>
              <p>{t('LABEL_USING_CATALOG_REFERENCES_TEXT_1')}</p>
              <p>{t('LABEL_USING_CATALOG_REFERENCES_TEXT_2')}</p>
            </div>
          </div>
          <TableContainer classes={{ root: styles.referencesContainer }}>
            <Table aria-label="references table">
              <colgroup>
                <col style={{ width: '30%' }} />
                <col style={{ width: '30%' }} />
                <col style={{ width: '30%' }} />
                <col style={{ width: '10%' }} />
              </colgroup>
              <TableHead>
                <TableRow classes={{ root: styles.tableHeader }}>
                  <TableCell>
                    <div>{t('LABEL_CATALOG_REFERENCE_1')}</div>
                    <div>{t('LABEL_CATALOG_REFERENCE_2')}</div>
                  </TableCell>
                  <TableCell>{t('LABEL_SIZE')}</TableCell>
                  <TableCell>{t('LABEL_QUANTITY')}</TableCell>
                  <TableCell>{t('LABEL_DELETE')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {references?.map((_, iterator) => (
                  <Reference
                    key={`ref_${iterator}`}
                    row={iterator}
                    updateReference={updateReference}
                  />
                ))}
                <TableRow classes={{ root: styles.addReference }}>
                  <TableCell colSpan={4}>
                    <div>
                      <button onClick={handleAdd}>
                        <span>{t('LABEL_ENTER_ANOTHER_REFERENCE')}</span>
                        <img
                          src={PlusIcon}
                          alt="add"
                          className={styles.plusIcon}
                        />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <div className={styles.addItems}>
            <button
              className={!hasReference ? styles.disabled : ''}
              onClick={handleSubmit}
            >
              {t('LABEL_ADD_ITEMS_TO_BASKET')}
            </button>
          </div>
        </div>
        <div className={styles.rightSide}>
          <PrivilegeCode />
        </div>
      </div>
      <ModalLoading isOpen={open !== ''} />
      <Footer />
    </div>
  );
}

function RenderMetadata(): JSX.Element {
  const { t } = useTranslation(['directOrderPage']);

  return (
    <Helmet>
      <title>
        {t('META_TITLE_TEMPLATE', {
          title: t('DIRECT_ORDER_META_TITLE'),
          ns: 'translation',
        })}
      </title>
      <meta name="description" content={t('DIRECT_ORDER_META_DESCRIPTION')} />
    </Helmet>
  );
}
